const PLAYER_SELECTOR = 'starry-action-player';
const LOAD_TIMEOUT_MS = 12000;
const PREWARM_ROOT_MARGIN = '600px 0px';

const connection = navigator.connection || navigator.mozConnection || navigator.webkitConnection;
const shouldLimitPreloading =
  connection?.saveData || connection?.effectiveType === 'slow-2g' || connection?.effectiveType === '2g';

const prewarmObserver =
  !shouldLimitPreloading && 'IntersectionObserver' in window
    ? new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (!entry.isIntersecting) return;

            entry.target.prewarmVideo();
            prewarmObserver.unobserve(entry.target);
          });
        },
        { rootMargin: PREWARM_ROOT_MARGIN }
      )
    : null;

/**
 * Pause every other social-proof clip whenever one starts or resumes.
 *
 * Listening for the native play event also covers playback started from the
 * browser's controls after the initial poster interaction.
 */
document.addEventListener(
  'play',
  (event) => {
    const activeVideo = event.target;

    if (!(activeVideo instanceof HTMLVideoElement) || !activeVideo.closest(PLAYER_SELECTOR)) return;

    document.querySelectorAll(`${PLAYER_SELECTOR} video`).forEach((video) => {
      if (video !== activeVideo) video.pause();
    });
  },
  true
);

class StarryActionPlayer extends HTMLElement {
  constructor() {
    super();
    this.abortController = null;
    this.loadTimeout = null;
    this.playButton = null;
    this.status = null;
    this.video = null;
  }

  connectedCallback() {
    this.abortController?.abort();
    this.abortController = new AbortController();
    const { signal } = this.abortController;

    this.playButton = this.querySelector('[data-starry-action-play]');
    this.status = this.querySelector('[data-starry-action-status]');
    this.video = this.querySelector('video');

    this.playButton?.addEventListener('click', () => this.loadAndPlay(), { signal });
    this.addEventListener('pointerenter', () => this.prewarmVideo(), { signal });
    this.addEventListener('pointerdown', () => this.prewarmVideo(), { signal });
    this.addEventListener('focusin', () => this.prewarmVideo(), { signal });

    if (this.video) {
      this.bindVideoEvents(this.video);
      prewarmObserver?.observe(this);
    }
  }

  disconnectedCallback() {
    this.abortController?.abort();
    prewarmObserver?.unobserve(this);
    this.clearLoadTimeout();
    this.video?.pause();
  }

  loadAndPlay() {
    if (!(this.playButton instanceof HTMLButtonElement)) return;

    const video = this.video;

    if (!(video instanceof HTMLVideoElement)) {
      this.showFailure();
      return;
    }

    this.prewarmVideo();
    this.playButton.disabled = true;
    this.setAttribute('aria-busy', 'true');
    this.setStatus(this.dataset.loadingMessage || 'Loading video.');
    this.startLoadTimeout();
    video.removeAttribute('aria-hidden');
    video.tabIndex = 0;

    const playPromise = video.play();

    if (playPromise) {
      playPromise.catch(() => {
        if (this.video !== video) return;

        if (video.error) {
          this.showFailure();
          return;
        }

        this.revealControls();
      });
    }
  }

  prewarmVideo() {
    if (!(this.video instanceof HTMLVideoElement) || this.video.preload === 'auto') return;

    this.video.preload = 'auto';
    this.video.load();
  }

  bindVideoEvents(video) {
    const signal = this.abortController?.signal;

    video.addEventListener('playing', () => this.revealControls(true), { signal });
    video.addEventListener('error', () => this.showFailure(), { signal });
  }

  revealControls(isPlaying = false) {
    this.clearLoadTimeout();
    this.removeAttribute('aria-busy');
    this.classList.add('starry-action-card__player--active');

    if (this.playButton instanceof HTMLButtonElement) {
      this.playButton.hidden = true;
      this.playButton.disabled = false;
    }

    this.setStatus(isPlaying ? '' : this.dataset.controlsMessage || 'Use the video controls to play.');

    if (this.video) {
      this.video.removeAttribute('aria-hidden');
      this.video.tabIndex = 0;
      this.video.focus({ preventScroll: true });
    }
  }

  showFailure() {
    this.clearLoadTimeout();
    this.removeAttribute('aria-busy');
    this.classList.remove('starry-action-card__player--active');

    this.video?.remove();
    this.video = null;

    if (this.playButton instanceof HTMLButtonElement) {
      this.playButton.hidden = false;
      this.playButton.disabled = true;
      this.playButton.querySelector('[data-starry-action-play-icon]')?.setAttribute('hidden', '');
      this.playButton.querySelector('[data-starry-action-unavailable]')?.removeAttribute('hidden');
    }

    this.setStatus(this.dataset.errorMessage || 'Video unavailable. The poster image is still shown.');
  }

  startLoadTimeout() {
    this.clearLoadTimeout();
    this.loadTimeout = window.setTimeout(() => this.revealControls(), LOAD_TIMEOUT_MS);
  }

  clearLoadTimeout() {
    if (!this.loadTimeout) return;

    window.clearTimeout(this.loadTimeout);
    this.loadTimeout = null;
  }

  setStatus(message) {
    if (this.status) this.status.textContent = message;
  }
}

if (!customElements.get(PLAYER_SELECTOR)) {
  customElements.define(PLAYER_SELECTOR, StarryActionPlayer);
}
